var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};
function _objectWithoutProperties(obj, keys) {
  var target = {};
  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }
  return target;
}
function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}
function _possibleConstructorReturn(self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }
  return call && (typeof call === "object" || typeof call === "function") ? call : self;
}
function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }
  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
}
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import isValidFocusKeypress from '../utils/EventUtils/isValidFocusKeypress';
import setRef from '../utils/ref';
var BASE_FOCUSABLE_ELEMENTS = '[href],[tabindex]:not([tabindex="-1"]),input:not([disabled]):not([type="hidden"])';
var FOCUSABLE_QUERY = ['button', 'textarea', 'select'].reduce(function (queryString, element) {
  return queryString + ',' + element + ':not([disabled])';
}, BASE_FOCUSABLE_ELEMENTS);

/**
 * This component is used for keeping the focus within some container. When the container
 * is mounted and the `focusOnMount` prop is `true`, it will attempt to focus either:
 * - an element that matches `document.getElementById(this.props.initialFocus)`
 * - an element that matches `this._container.querySelector(this.props.initialFocus)`
 * - the first focusable element in it's children (if `this.props.initialFocus` is omitted)
 */

var FocusContainer = function (_PureComponent) {
  _inherits(FocusContainer, _PureComponent);
  function FocusContainer() {
    var _temp, _this, _ret;
    _classCallCheck(this, FocusContainer);
    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return _ret = (_temp = (_this = _possibleConstructorReturn(this, _PureComponent.call.apply(_PureComponent, [this].concat(args))), _this), _this._enableFocusTrap = function () {
      window.addEventListener('keydown', _this._handleKeyDown, true);
    }, _this._disableFocusTrap = function () {
      window.removeEventListener('keydown', _this._handleKeyDown, true);
    }, _this._attemptInitialFocus = function () {
      if (!_this._container) {
        return;
      }
      var initialFocus = _this.props.initialFocus;
      var toFocus = initialFocus ? document.getElementById(initialFocus) || _this._container.querySelector(initialFocus) : _this._focusables[0];
      var debugError = void 0;
      if (!toFocus && initialFocus) {
        debugError = ' The `initialFocus` did not match a document\'s `id` or was an invalid ';
        debugError += '`querySelector` for the container. `initialFocus`: `' + initialFocus + '`. ';
        debugError += 'If this was supposed to be an `id`, make sure to prefix with the `#` symbol.';
      }
      if (process.env.NODE_ENV !== 'production' && !toFocus) {
        throw new Error('You specified that the `FocusContainer` should focus an element on mount, ' + 'but a focusable element was not found in the children. This could be because ' + 'the `initialFocus` prop is an invalid id or query selector, or the children ' + ('do not contain a valid focusable element.' + debugError));
      }
      if (toFocus) {
        toFocus.focus();
      }
    }, _this._containFocus = function (containerRef) {
      var _this2 = _this,
        props = _this2.props;
      setRef(props.componentRef, containerRef);
      if (containerRef === null) {
        _this._container = null;
        _this._disableFocusTrap();
        setRef(props.containerRef, null);
        return;
      }
      var focusOnMount = props.focusOnMount,
        containFocus = props.containFocus;
      _this._container = findDOMNode(containerRef);
      setRef(props.containerRef, _this._container);
      _this._focusables = [].slice.call(_this._container.querySelectorAll(FOCUSABLE_QUERY));
      if (focusOnMount) {
        _this._attemptInitialFocus();
      }
      if (containFocus) {
        _this._enableFocusTrap();
      }
    }, _this._handleKeyDown = function (e) {
      _this._shifted = e.shiftKey;
      if (!isValidFocusKeypress(e, _this.props.additionalFocusKeys)) {
        return;
      } else if (_this._focusables.length === 1) {
        e.preventDefault();
        return;
      }
      var target = e.target,
        shiftKey = e.shiftKey;
      var _this$_focusables = _this._focusables,
        first = _this$_focusables[0],
        focusables = _this$_focusables.slice(1);
      var last = focusables[focusables.length - 1];
      if (shiftKey && target === first) {
        e.preventDefault();
        last.focus();
      } else if (!shiftKey && target === last) {
        e.preventDefault();
        first.focus();
      }
    }, _temp), _possibleConstructorReturn(_this, _ret);
  }
  FocusContainer.prototype.componentWillReceiveProps = function componentWillReceiveProps(nextProps) {
    if (this.props.containFocus === nextProps.containFocus) {
      return;
    }
    if (nextProps.containFocus) {
      this._enableFocusTrap();
      this._attemptInitialFocus();
    } else {
      this._disableFocusTrap();
    }
  };
  FocusContainer.prototype.componentDidUpdate = function componentDidUpdate() {
    if (this.props.containFocus && this._container) {
      this._focusables = [].slice.call(this._container.querySelectorAll(FOCUSABLE_QUERY));
    }
  };
  FocusContainer.prototype.componentWillUnmount = function componentWillUnmount() {
    if (this.props.containFocus) {
      this._disableFocusTrap();
    }
  };

  /**
   * Return reference to the top-most DOM node of the rendered container.
   *
   * @return {Element|null|undefined} Reference to the top-most DOM node of the rendered container.
   */

  FocusContainer.prototype.getContainer = function getContainer() {
    return this._container;
  };

  /**
   * Manages the event listeners to contain the focus within some container.  When the container
   * ref is not null, the container has mounted and then attempts to focus an element inside
   * if the `focusOnMount` prop is `true`.
   */

  FocusContainer.prototype.render = function render() {
    var _props = this.props,
      Component = _props.component,
      componentRef = _props.componentRef,
      containerRef = _props.containerRef,
      initialFocus = _props.initialFocus,
      focusOnMount = _props.focusOnMount,
      containFocus = _props.containFocus,
      additionalFocusKeys = _props.additionalFocusKeys,
      props = _objectWithoutProperties(_props, ['component', 'componentRef', 'containerRef', 'initialFocus', 'focusOnMount', 'containFocus', 'additionalFocusKeys']);
    return React.createElement(Component, _extends({}, props, {
      ref: this._containFocus
    }));
  };
  return FocusContainer;
}(PureComponent);
FocusContainer.propTypes = {
  /**
   * The component to render as. This can be a React DOM element or
   * a react Component.
   */
  component: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]).isRequired,
  /**
   * An optional ref to get reference to the rendered component.
   * Just like other refs, this will provide null when it unmounts.
   */
  componentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  /**
   * An optional ref to get reference to the top-most element of the rendered container.
   * Just like other refs, this will provide null when it unmounts.
   *
   * This is helpful if you'd like access the DOM node for a parent Component without needing to use
   * `ReactDOM.findDOMNode`.
   */
  containerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  /**
   * An optional style to apply.
   */
  style: PropTypes.object,
  /**
   * An optional className to apply.
   */
  className: PropTypes.string,
  /**
   * The children to display.
   */
  children: PropTypes.node,
  /**
   * An optional id string or a query selector string to use for the initial focus.
   * This will only be triggered if the `focusOnMount` prop is `true`. If this is
   * omitted and the `focusOnMount` prop is `true`, the first focusable element in the
   * container will be focused.
   *
   * Examples:
   *
   * ```js
   * initialFocus="#someAmazingId"
   * // or
   * initialFocus=".md-btn,.md-list-tile"
   * ```
   */
  initialFocus: PropTypes.string,
  /**
   * Boolean if an element in the container should be focused when mounted.
   */
  focusOnMount: PropTypes.bool,
  /**
   * An optional list of additional key codes to use for focus events.
   */
  additionalFocusKeys: PropTypes.arrayOf(PropTypes.number),
  /**
   * Boolean if the focus container should start or stop containing the focus within the container.
   * This is useful for changing the focus requirements after mount.
   */
  containFocus: PropTypes.bool
};
FocusContainer.defaultProps = {
  component: 'div',
  containFocus: true
};
export default FocusContainer;